import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Chip,
  useTheme,
  Autocomplete,
  TextField,
  Tooltip,
  Select,
  MenuItem,
  Paper,
  Fade,
  Zoom,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Collapse,
  IconButton,
  Card,
  CardContent,
  CardActionArea,
  Badge,
  useMediaQuery,
  alpha,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Autorenew as AutorenewIcon,
  ArrowForward as ArrowForwardIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  SwapHorizontalCircle as SwapIcon,
  FoodBank as FoodBankIcon,
  LocalDrink as LocalDrinkIcon,
  Kitchen as KitchenIcon,
  Search as SearchIcon,
  Opacity as OpacityIcon,
  FreeBreakfast as FreeBreakfastIcon,
  Spa as SpaIcon,
  Restaurant as RestaurantIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { keyframes, width } from "@mui/system";
import PropTypes from "prop-types";
import { cookingTasksValidation } from "../Validation/cookingTasksValidation";
import { useGetMe } from "../Context/GetMeContext";
import { useKitchenDetail } from "../Context/GetMyKitchenDetailContext";
import CustomModal from "./CustomModal";
import InventoryEditBox from "./inventory/InventoryEditBox";
import { ingredientChipTasksMap, validateTaskType } from "../constants";
import { tasks as staticTasks } from "../Static/task";
import SwapDialog from "./Dialogs/SwapDialog";
import ManualEntryDialog from "./Dialogs/ManualEntryDialog";

const SWAP_VALIDATION = {
  choose_spices: ["choose_spices", "choose_bowl", "choose_cup"],
  choose_soaks: ["choose_soaks", "choose_bowl", "choose_cup"],
  choose_grains: ["choose_grains", "choose_bowl", "choose_cup"],
  choose_oil: ["choose_oil", "choose_bowl", "choose_cup"],
  choose_paste: ["choose_paste", "choose_bowl", "choose_cup"],
  choose_bowl: ["choose_bowl", "choose_cup"],
  choose_cup: ["choose_cup", "choose_bowl"],
};

// Container type icons and friendly names
const getContainerTypeInfo = (theme) => ({
  spice: {
    icon: <SpaIcon />,
    name: "Spice Box",
    color: theme.palette.error.main,
    description: "For spices and seasonings",
  },
  grain: {
    icon: <RestaurantIcon />,
    name: "Grain Container",
    color: theme.palette.warning.main,
    description: "For rice, pasta, lentils",
  },
  paste: {
    icon: <FreeBreakfastIcon />,
    name: "Paste Container",
    color: theme.palette.grey[600],
    description: "For sauces and pastes",
  },
  oil: {
    icon: <OpacityIcon />,
    name: "Oil Dispenser",
    color: theme.palette.warning.light,
    description: "For oils and liquid ingredients",
  },
  bowl: {
    icon: <KitchenIcon />,
    name: "Mixing Bowl",
    color: theme.palette.info.main,
    description: "For mixing ingredients",
  },
  cup: {
    icon: <LocalDrinkIcon />,
    name: "Measuring Cup",
    color: theme.palette.success.main,
    description: "For measured ingredients",
  },
  manualbowl: {
    icon: <KitchenIcon />,
    name: "Bowl",
    color: theme.palette.info.main,
    description: "Manual bowl station",
  },
  manualcup: {
    icon: <LocalDrinkIcon />,
    name: "Cup",
    color: theme.palette.success.main,
    description: "Manual cup station",
  },
  soak: {
    icon: <FoodBankIcon />,
    name: "Soaking Bowl",
    color: theme.palette.secondary.main,
    description: "For soaking ingredients",
  },
});

// Enhanced animations
const glowEffect = keyframes`
  0% { box-shadow: 0 0 5px rgba(255, 0, 0, 0.3); }
  50% { box-shadow: 0 0 20px rgba(255, 0, 0, 0.8); }
  100% { box-shadow: 0 0 5px rgba(255, 0, 0, 0.3); }
`;

const pulseSuccess = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

// Sub-components
const TaskStatusIcon = React.memo(({ isOk, theme }) => (
  <Zoom in>
    {isOk ? (
      <Avatar
        sx={{
          bgcolor: "success.light",
          width: 36,
          height: 36,
          mr: 1.5,
          animation: `${pulseSuccess} 2s ease-in-out`,
        }}
      >
        <CheckCircleIcon sx={{ color: "white", fontSize: 22 }} />
      </Avatar>
    ) : (
      <Avatar
        sx={{
          bgcolor: "error.light",
          width: 36,
          height: 36,
          mr: 1.5,
          animation: `${glowEffect} 3s infinite`,
        }}
      >
        <ErrorIcon sx={{ color: "white", fontSize: 22 }} />
      </Avatar>
    )}
  </Zoom>
));

const ErrorMessageChip = React.memo(({ errorMessage, onClick, showRefill }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Only show refill button if inventory exists but is low
  if (!showRefill) {
    return (
      <Zoom in>
        <Chip
          icon={<ErrorIcon sx={{ color: "inherit" }} />}
          label={errorMessage}
          sx={{
            fontWeight: 600,
            px: 1,
            py: isMobile ? 0.5 : 1.2,
            maxWidth: { xs: "100%", sm: "auto" },
            borderRadius: "16px",
            backgroundColor: "error.main",
            color: "common.white",
            boxShadow: 2,
            transition: "all 0.3s ease",
            "& .MuiChip-label": {
              whiteSpace: "normal",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            },
          }}
        />
      </Zoom>
    );
  }

  return (
    <Zoom in>
      <Chip
        icon={
          <Tooltip title="Click to refill this ingredient">
            <AutorenewIcon sx={{ color: "inherit" }} />
          </Tooltip>
        }
        label={`Refill ${errorMessage}`}
        onClick={onClick}
        sx={{
          fontWeight: 600,
          px: 1,
          py: isMobile ? 0.5 : 1.2,
          maxWidth: { xs: "100%", sm: "auto" },
          borderRadius: "16px",
          backgroundColor: "error.main",
          color: "common.white",
          boxShadow: 2,
          transition: "all 0.3s ease",
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "error.dark",
            transform: "translateY(-2px)",
            boxShadow: 4,
          },
          "& .MuiChip-label": {
            whiteSpace: "normal",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          },
        }}
      />
    </Zoom>
  );
});

// Create a memoized TaskInfo component
const TaskInfo = React.memo(({ task, isReady }) => {
  return (
    <Box
      sx={{
        flexGrow: 1,
        minWidth: 0, // Important for text overflow handling
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: "text.primary",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: { xs: "normal", sm: "nowrap" },
            wordBreak: "break-word",
          }}
        >
          {task.item_name}
        </Typography>

        {isReady && (
          <Chip
            icon={<EditIcon fontSize="small" />}
            label="Click to Edit"
            size="small"
            color="success"
            variant="outlined"
            sx={{
              height: "20px",
              fontSize: "0.7rem",
              "& .MuiChip-label": {
                px: 0.5,
              },
            }}
          />
        )}
      </Box>

      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          fontWeight: 500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: { xs: "normal", sm: "nowrap" },
        }}
      >
        Quantity:{" "}
        <Box component="span" sx={{ fontWeight: 700 }}>
          {task.quantity} {task.unit}
        </Box>
      </Typography>
    </Box>
  );
});

TaskInfo.displayName = "TaskInfo";

// Task status updating after refill
const updateTaskStatusAfterRefill = (
  updatedBox,
  matchInventoryWithTasks,
  tasks,
  showNotification,
  refreshInventory
) => {
  console.log("Inventory updated:", updatedBox);

  // First notify the user about successful update
  if (typeof showNotification === "function") {
    showNotification(
      "success",
      `Inventory for ${updatedBox.item_name} updated successfully`
    );
  }

  // Return a promise to ensure operations complete in sequence
  return new Promise((resolve) => {
    // Use animation frame for smoother transition
    requestAnimationFrame(() => {
      // First, run the refresh operation
      if (typeof refreshInventory === "function") {
        console.log("Calling refreshInventory to get fresh data");

        // Wait for inventory refresh to complete before resolving
        refreshInventory();

        // Give time for the refresh to complete and state to update
        setTimeout(resolve, 400);
      } else if (typeof matchInventoryWithTasks === "function") {
        console.log("Manually refreshing task status with updated inventory");
        // Create a new array to ensure React detects the change
        const updatedTasks = [...tasks];
        matchInventoryWithTasks(updatedTasks);

        // Give some time for state to update
        setTimeout(resolve, 300);
      } else {
        resolve();
      }
    });
  });
};

// Container type selection - updated with icons and cards
const ContainerTypeSelection = React.memo(
  ({ swapTypes, activeType, onSelect, theme }) => {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle2"
          color="text.secondary"
          gutterBottom
          sx={{
            fontWeight: "medium",
            mb: 1.5,
            display: "flex",
            alignItems: "center",
          }}
        >
          <SwapIcon sx={{ mr: 1, fontSize: 18 }} />
          Select the best container for this ingredient:
        </Typography>

        <Grid container spacing={2}>
          {swapTypes.map((type) => {
            const typeKey = validateTaskType(type)?.toLowerCase();
            const containerInfo = getContainerTypeInfo(theme)[typeKey] || {
              icon: <KitchenIcon />,
              name: validateTaskType(type),
              color: theme.palette.grey[500],
              description: "Container",
            };

            const isSelected = activeType === type;

            return (
              <Grid item xs={12} sm={6} md={6} key={type}>
                <Card
                  raised={isSelected}
                  sx={{
                    cursor: "pointer",
                    height: "100%",
                    transition: "all 0.2s ease",
                    transform: isSelected ? "scale(1.03)" : "scale(1)",
                    bgcolor: isSelected
                      ? `${containerInfo.color}15`
                      : "background.paper",
                    borderLeft: isSelected
                      ? `4px solid ${containerInfo.color}`
                      : "none",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 4,
                      borderLeft: `4px solid ${containerInfo.color}`,
                    },
                  }}
                  onClick={() => onSelect(type)}
                >
                  <CardContent sx={{ py: 2, px: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Avatar
                        sx={{
                          bgcolor: isSelected
                            ? containerInfo.color
                            : "action.selected",
                          width: 40,
                          height: 40,
                        }}
                      >
                        {React.cloneElement(containerInfo.icon, {
                          fontSize: "small",
                        })}
                      </Avatar>
                      <Typography
                        variant="body1"
                        textTransform={"capitalize"}
                        sx={{
                          ml: 1,
                          fontWeight: isSelected ? "bold" : "medium",
                          color: isSelected ? "primary.main" : "text.primary",
                        }}
                      >
                        {containerInfo.name}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        mt: 1,
                        pb: 1,
                        borderBottom: `1px solid ${
                          isSelected ? containerInfo.color : "transparent"
                        }`,
                      }}
                    >
                      {containerInfo.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  }
);

ContainerTypeSelection.displayName = "ContainerTypeSelection";

// BoxSelection component for the accordion - improved with status indicators and better visuals
const BoxSelection = React.memo(
  ({
    boxes,
    taskType,
    onSelect,
    stationSelected,
    showNotification,
    currentTasks = [],
  }) => {
    const theme = useTheme();
    const [manualPosition, setManualPosition] = useState(1);
    const [showUsedPositions, setShowUsedPositions] = useState(true);
    console.log("boxes", boxes);
    console.log("taskType", taskType);
    console.log("onSelect type:", typeof onSelect);

    // Get any previously used positions from localStorage for validation
    const [usedPositions, setUsedPositions] = useState([]);
    const [currentRecipePositions, setCurrentRecipePositions] = useState([]);

    useEffect(() => {
      // Check for previously used positions for bowls and cups
      try {
        const previousKitchenTask = localStorage.getItem("kitchenTask");
        let previousUsed = [];

        if (previousKitchenTask) {
          const parsedTask = JSON.parse(previousKitchenTask);
          if (
            parsedTask &&
            parsedTask.tasks &&
            Array.isArray(parsedTask.tasks)
          ) {
            // Extract positions used by bowls and cups in previous recipes
            previousUsed = parsedTask.tasks
              .filter(
                (t) =>
                  (t.type === "choose_bowl" || t.type === "choose_cup") &&
                  t.position
              )
              .map((t) => ({
                type: t.type,
                position: t.position,
                name: t.item_name,
                source: "previous",
              }));
          }
        }

        // Extract positions used in current recipe
        const currentUsed = currentTasks
          .filter(
            (t) =>
              (t.type === "choose_bowl" || t.type === "choose_cup") &&
              t.position
          )
          .map((t) => ({
            type: t.type,
            position: t.position,
            name: t.item_name,
            source: "current",
          }));

        setUsedPositions(previousUsed);
        setCurrentRecipePositions(currentUsed);
      } catch (error) {
        console.error("Error loading used positions:", error);
      }
    }, [currentTasks]);

    // Filter boxes by type
    const validBoxes = useMemo(() => {
      const validatedType = validateTaskType(taskType);
      if (!validatedType || !boxes?.length) return [];
      console.log("boxes", boxes);

      return boxes
        .filter(
          (box) =>
            box.inventory_type?.toLowerCase() === validatedType?.toLowerCase()
        )
        .sort((a, b) => a.box_no - b.box_no);
    }, [boxes, taskType]);

    // Handle manual position change
    const handleManualPositionChange = (e) => {
      const value = parseInt(e.target.value);
      if (!isNaN(value) && value > 0) {
        setManualPosition(value);
      }
    };

    // Check if a position is used in previous recipe
    const isPositionUsedInPrevious = (boxNo) => {
      if (taskType !== "choose_bowl" && taskType !== "choose_cup") {
        return false;
      }
      return usedPositions.some(
        (p) => p.type === taskType && String(p.position) === String(boxNo)
      );
    };

    // Check if a position is used in current recipe
    const isPositionUsedInCurrent = (boxNo) => {
      if (taskType !== "choose_bowl" && taskType !== "choose_cup") {
        return false;
      }
      return currentRecipePositions.some(
        (p) => p.type === taskType && String(p.position) === String(boxNo)
      );
    };

    // Get info about used position
    const getUsedPositionInfo = (boxNo) => {
      if (taskType !== "choose_bowl" && taskType !== "choose_cup") {
        return null;
      }

      // Check current recipe first
      const currentUsed = currentRecipePositions.find(
        (p) => p.type === taskType && String(p.position) === String(boxNo)
      );
      if (currentUsed) return currentUsed;

      // Then check previous recipes
      const previousUsed = usedPositions.find(
        (p) => p.type === taskType && String(p.position) === String(boxNo)
      );
      return previousUsed;
    };

    // Safe box selection handler - with better user messaging
    const handleBoxSelect = (boxNo) => {
      const usedInCurrent = isPositionUsedInCurrent(boxNo);
      const usedInPrevious = isPositionUsedInPrevious(boxNo);
      const usedInfo = getUsedPositionInfo(boxNo);

      if (usedInCurrent) {
        // Position used in current recipe - show warning but allow override
        const message = `Position ${boxNo} is already used for "${usedInfo.name}" in this recipe. Do you want to reassign it?`;

        if (window.confirm(message)) {
          if (typeof onSelect === "function") {
            onSelect(boxNo, taskType);
            if (typeof showNotification === "function") {
              showNotification(
                "warning",
                `Position ${boxNo} reassigned from "${usedInfo.name}"`
              );
            }
          }
        }
        return;
      }

      if (usedInPrevious) {
        // Position used in previous recipe - show info but allow selection
        const message = `Position ${boxNo} was used for "${usedInfo.name}" in a previous recipe. You can still use it for this ingredient.`;

        if (typeof showNotification === "function") {
          showNotification("info", message, 4000);
        }
      }

      // Safe call to onSelect
      if (typeof onSelect === "function") {
        onSelect(boxNo, taskType);
      } else {
        console.error("onSelect is not a function", onSelect);
      }
    };

    // Handle manual position selection with better validation
    const handleManualSelection = () => {
      const usedInCurrent = isPositionUsedInCurrent(manualPosition);
      const usedInPrevious = isPositionUsedInPrevious(manualPosition);
      const usedInfo = getUsedPositionInfo(manualPosition);

      if (usedInCurrent) {
        const message = `Position ${manualPosition} is already used for "${usedInfo.name}" in this recipe. Do you want to reassign it?`;

        if (window.confirm(message)) {
          if (typeof onSelect === "function") {
            onSelect(manualPosition, taskType);
            if (typeof showNotification === "function") {
              showNotification(
                "warning",
                `Position ${manualPosition} reassigned from "${usedInfo.name}"`
              );
            }
          }
        }
        return;
      }

      if (usedInPrevious) {
        if (typeof showNotification === "function") {
          showNotification(
            "info",
            `Position ${manualPosition} was used in previous recipe but is available now`,
            3000
          );
        }
      }

      if (typeof onSelect === "function") {
        onSelect(manualPosition, taskType);
      }
    };

    // Get container type info for styling
    const typeKey = validateTaskType(taskType)?.toLowerCase();
    const containerInfo = getContainerTypeInfo(theme)[typeKey] || {
      icon: <KitchenIcon />,
      name: validateTaskType(taskType),
      color: theme.palette.grey[500],
    };

    return (
      <Box sx={{ mt: 2 }}>
        <Typography
          variant="subtitle2"
          gutterBottom
          sx={{
            display: "flex",
            alignItems: "center",
            color: "text.secondary",
            mb: 2,
          }}
        >
          <KitchenIcon sx={{ mr: 1, fontSize: 18 }} />
          {validBoxes.length > 0
            ? `Select ${containerInfo.name} position:`
            : "No available containers found"}
        </Typography>

        {(taskType === "choose_bowl" || taskType === "choose_cup") &&
          (usedPositions.length > 0 || currentRecipePositions.length > 0) && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                bgcolor: alpha(theme.palette.info.main, 0.1),
                borderRadius: 2,
                border: "1px solid",
                borderColor: alpha(theme.palette.info.main, 0.3),
              }}
            >
              <Typography
                variant="subtitle2"
                color="info.main"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 1.5,
                  fontWeight: "bold",
                }}
              >
                <KitchenIcon sx={{ fontSize: 18, mr: 1 }} />
                Position Usage Summary
              </Typography>

              {/* Current Recipe Usage */}
              {currentRecipePositions.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    color="warning.main"
                    gutterBottom
                  >
                    Used in Current Recipe:
                  </Typography>
                  {currentRecipePositions
                    .filter((p) => p.type === taskType)
                    .map((pos, index) => (
                      <Chip
                        key={index}
                        label={`Position ${pos.position}: ${pos.name}`}
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ mr: 1, mb: 0.5 }}
                      />
                    ))}
                </Box>
              )}

              {/* Previous Recipe Usage */}
              {usedPositions.length > 0 && (
                <Box>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    color="info.main"
                    gutterBottom
                  >
                    Used in Previous Recipes:
                  </Typography>
                  {usedPositions
                    .filter((p) => p.type === taskType)
                    .map((pos, index) => (
                      <Chip
                        key={index}
                        label={`Position ${pos.position}: ${pos.name}`}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ mr: 1, mb: 0.5 }}
                      />
                    ))}
                </Box>
              )}

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                💡 You can reassign positions from current recipe or reuse
                positions from previous recipes
              </Typography>
            </Box>
          )}

        {validBoxes.length === 0 ? (
          <Box
            sx={{
              p: 3,
              textAlign: "center",
              border: "2px dashed",
              borderColor: "warning.light",
              borderRadius: 2,
              backgroundColor: "warning.lighter",
              my: 2,
            }}
          >
            <SearchIcon sx={{ fontSize: 40, color: "warning.main", mb: 1 }} />
            <Typography
              color="warning.dark"
              variant="subtitle1"
              gutterBottom
              fontWeight="bold"
            >
              No {containerInfo.name} containers found
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: "100%" }}
            >
              You can assign a manual position below:
            </Typography>

            <Box
              sx={{
                display: "flex",
                mt: 2,
                justifyContent: "center",
                gap: 2,
                alignItems: "center",
              }}
            >
              <TextField
                label="Position"
                type="number"
                size="small"
                value={manualPosition}
                onChange={handleManualPositionChange}
                InputProps={{
                  inputProps: { min: 1 },
                }}
                sx={{ width: "100px" }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleManualSelection}
                startIcon={containerInfo.icon}
                disabled={isPositionUsedInPrevious(manualPosition)}
              >
                Assign Position
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <Grid container spacing={2}>
              {validBoxes.map((box) => {
                console.log("box", box);
                // Check if box has content
                const hasContent =
                  box.item_name !== "Unlabeled Box" && box.box_filled > 0;
                const isEmpty = box.item_name === "Unlabeled Box";

                // Check usage states
                const usedInCurrent = isPositionUsedInCurrent(box.box_no);
                const usedInPrevious = isPositionUsedInPrevious(box.box_no);
                const usedInfo = getUsedPositionInfo(box.box_no);

                // Determine badge and status
                let badgeText = "Available";
                let badgeColor = "success";
                let clickable = true;

                if (usedInCurrent) {
                  badgeText = "Current Recipe";
                  badgeColor = "warning";
                } else if (usedInPrevious) {
                  badgeText = "Previous Recipe";
                  badgeColor = "info";
                } else if (hasContent) {
                  badgeText = "Has Content";
                  badgeColor = "secondary";
                }

                return (
                  <Grid item xs={6} sm={6} md={6} key={box.id}>
                    <Badge
                      badgeContent={badgeText}
                      color={badgeColor}
                      sx={{
                        width: "100%",
                        "& .MuiBadge-badge": {
                          fontSize: "0.6rem",
                          minWidth: "auto",
                          height: "16px",
                          padding: "0 4px",
                          borderRadius: "8px",
                          transform: "scale(0.85)",
                          right: "8px",
                          top: "8px",
                          maxWidth: "80px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        },
                      }}
                      overlap="rectangular"
                      anchorOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                    >
                      <Card
                        variant={isEmpty ? "outlined" : "elevation"}
                        elevation={isEmpty ? 0 : 2}
                        sx={{
                          borderColor: usedInCurrent
                            ? "warning.main"
                            : usedInPrevious
                            ? "info.main"
                            : containerInfo.color,
                          borderWidth: 2,
                          transition: "all 0.2s ease",
                          width: "100%",
                          opacity: clickable ? 1 : 0.7,
                          position: "relative",
                          "&:hover": clickable
                            ? {
                                transform: "translateY(-4px)",
                                boxShadow: 3,
                                borderColor: usedInCurrent
                                  ? "warning.dark"
                                  : usedInPrevious
                                  ? "info.dark"
                                  : containerInfo.color,
                              }
                            : {},
                        }}
                      >
                        <CardActionArea
                          onClick={() => handleBoxSelect(box.box_no)}
                          disabled={false} // Always allow clicking for better UX
                          sx={{
                            p: 1.5,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            height: "100%",
                            cursor: "pointer",
                          }}
                        >
                          <Typography
                            variant="h5"
                            color={
                              usedInCurrent
                                ? "warning.main"
                                : usedInPrevious
                                ? "info.main"
                                : "primary.main"
                            }
                            sx={{ fontWeight: "bold" }}
                          >
                            {box.box_no}
                          </Typography>

                          {(usedInCurrent || usedInPrevious || !isEmpty) && (
                            <Box
                              sx={{
                                width: "100%",
                                mt: 1,
                                textAlign: "center",
                                overflow: "hidden",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: "block",
                                  whiteSpace: "wrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: "100%",
                                }}
                              >
                                {usedInfo ? usedInfo.name : box.item_name}
                              </Typography>

                              {(usedInCurrent || usedInPrevious) && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: usedInCurrent
                                      ? "warning.main"
                                      : "info.main",
                                    display: "block",
                                    fontSize: "0.7rem",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {usedInCurrent
                                    ? "Used in this recipe"
                                    : "Used in previous recipe"}
                                </Typography>
                              )}

                              {box.box_filled > 0 &&
                                !usedInCurrent &&
                                !usedInPrevious && (
                                  <Box
                                    sx={{
                                      height: 4,
                                      width: "100%",
                                      bgcolor: "background.paper",
                                      mt: 0.5,
                                      borderRadius: 1,
                                      overflow: "hidden",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        height: "100%",
                                        width: `${
                                          (box.box_filled / box.box_capacity) *
                                          100
                                        }%`,
                                        bgcolor: containerInfo.color,
                                        borderRadius: 1,
                                      }}
                                    />
                                  </Box>
                                )}
                            </Box>
                          )}
                        </CardActionArea>
                      </Card>
                    </Badge>
                  </Grid>
                );
              })}
            </Grid>

            {/* Manual position entry always available */}
            {/* <Box
              sx={{
                mt: 3,
                p: 2,
                borderTop: "1px dashed",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Or manually assign position:
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <TextField
                  label="Position #"
                  type="number"
                  size="small"
                  value={manualPosition}
                  onChange={handleManualPositionChange}
                  InputProps={{
                    inputProps: { min: 1 },
                  }}
                  sx={{ width: "100px" }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleManualSelection}
                  startIcon={containerInfo.icon}
                  disabled={isPositionUsedInPrevious(manualPosition)}
                >
                  Assign
                </Button>
              </Box>
            </Box> */}
          </>
        )}
      </Box>
    );
  }
);

BoxSelection.displayName = "BoxSelection";

// Main component
const TaskRow = React.memo(
  ({
    tasks,
    task,
    onManualEntry,
    onTaskSwap,
    inventoryData,
    refreshInventory,
    totaltasks,
    matchInventoryWithTasks,
    boxes,
    stationSelected,
    showNotification,
    isUnavailable = false,
  }) => {
    const theme = useTheme();
    const { user } = useGetMe();
    const { kitchenDetail } = useKitchenDetail();

    // State for swap options and modals
    const [expanded, setExpanded] = useState(false);
    const [activeSwapType, setActiveSwapType] = useState(null);
    const [isRefilling, setIsRefilling] = useState(false);
    const [showRefillModal, setShowRefillModal] = useState(false);
    const [selectedBoxForRefill, setSelectedBoxForRefill] = useState(null);
    const [isUserEditing, setIsUserEditing] = useState(false);
    const [wasReadyWhenOpened, setWasReadyWhenOpened] = useState(false);

    // Add a local implementation for manual entry in case the prop is missing
    const handleManualEntry = useCallback(
      (taskId, boxNo, selectedType) => {
        console.log(
          "Local handleManualEntry called:",
          taskId,
          boxNo,
          selectedType,
          typeof onManualEntry
        );

        // First try to use the prop if it exists
        if (typeof onManualEntry === "function") {
          console.log("onManualEntry function found");
          onManualEntry(taskId, boxNo, selectedType);
          // Close the accordion after successful assignment
          setExpanded(false);
          setActiveSwapType(null);

          // Check if we need to show inventory refill modal
          const inventoryTypes = [
            "choose_spices",
            "choose_grains",
            "choose_paste",
            "choose_oil",
            "choose_soaks",
          ];
          if (inventoryTypes.includes(selectedType)) {
            // Find the inventory box that matches the type and position
            const validatedType = validateTaskType(selectedType);
            const boxToUpdate = boxes?.find(
              (box) =>
                box.inventory_type?.toLowerCase() ===
                  validatedType?.toLowerCase() &&
                String(box.box_no) === String(boxNo)
            );

            if (boxToUpdate) {
              // Set the box for refill and show the modal
              setSelectedBoxForRefill({
                ...boxToUpdate,
                item_name: task.item_name, // Pre-set item name to current task
              });

              setTimeout(() => {
                setShowRefillModal(true);
              }, 300);
            }
          }

          return;
        }

        // Fallback implementation: update the local task
        if (onTaskSwap && typeof onTaskSwap === "function") {
          const updatedTasks = tasks.map((t) => {
            if (t.id === taskId) {
              return {
                ...t,
                position: String(boxNo),
                type: selectedType || t.type,
                isOk: true,
                errorMessage: null,
              };
            }
            return t;
          });

          onTaskSwap(updatedTasks);

          // Close the accordion after successful assignment
          setExpanded(false);
          setActiveSwapType(null);

          // Check if we need to show inventory refill modal
          const inventoryTypes = [
            "choose_spices",
            "choose_grains",
            "choose_paste",
            "choose_oil",
            "choose_soaks",
          ];
          if (inventoryTypes.includes(selectedType)) {
            // Find the inventory box that matches the type and position
            const validatedType = validateTaskType(selectedType);
            const boxToUpdate = boxes?.find(
              (box) =>
                box.inventory_type?.toLowerCase() ===
                  validatedType?.toLowerCase() &&
                String(box.box_no) === String(boxNo)
            );

            if (boxToUpdate) {
              // Set the box for refill and show the modal
              setSelectedBoxForRefill({
                ...boxToUpdate,
                item_name: task.item_name, // Pre-set item name to current task
              });

              setTimeout(() => {
                setShowRefillModal(true);
              }, 300);
            }
          }

          // Show notification about the manual assignment
          if (typeof showNotification === "function") {
            showNotification(
              "success",
              `Manual position ${boxNo} assigned to ${task.item_name}`
            );
          }
        }
      },
      [
        onManualEntry,
        onTaskSwap,
        tasks,
        showNotification,
        boxes,
        task,
        setSelectedBoxForRefill,
        setShowRefillModal,
        setExpanded,
        setActiveSwapType,
      ]
    );

    // Close expanded accordion if task is updated to be OK (but only for tasks that weren't ready when opened)
    useEffect(() => {
      // Only auto-close if:
      // 1. Task becomes ready AND
      // 2. It wasn't ready when the user opened it for editing AND
      // 3. User is not actively editing
      if (task.isOk && expanded && !wasReadyWhenOpened && !isUserEditing) {
        setExpanded(false);
        setActiveSwapType(null);
      }
    }, [task.isOk, expanded, wasReadyWhenOpened, isUserEditing]);

    // Handle manual close of accordion
    const handleClose = useCallback(() => {
      setIsUserEditing(false);
      setWasReadyWhenOpened(false);
      setExpanded(false);
      setActiveSwapType(null);
    }, []);

    // Get available swap types for this task
    const swapTypes = useMemo(() => {
      if (!task || !task.type) return [];
      return SWAP_VALIDATION[task.type] || [];
    }, [task]);

    // Get the display type for task category
    const taskCategory = useMemo(() => {
      const taskType = validateTaskType(task.type);
      switch (taskType?.toLowerCase()) {
        case "spice":
          return "Spice";
        case "grain":
          return "Grain";
        case "paste":
          return "Paste";
        case "oil":
          return "Oil";
        case "bowl":
          return "Bowl";
        case "cup":
          return "Cup";
        case "manualbowl":
          return "Bowl";
        case "manualcup":
          return "Cup";
        default:
          return task.type;
      }
    }, [task]);

    const handleBoxSelection = useCallback(
      (boxNo, selectedType) => {
        // Use our local implementation that has a fallback
        console.log("handleBoxSelection called:", task.id, boxNo, selectedType);
        handleManualEntry(task.id, boxNo, selectedType);

        // Reset editing state and close the accordion
        setIsUserEditing(false);
        setWasReadyWhenOpened(false);
        setExpanded(false);
        setActiveSwapType(null);
      },
      [task.id, handleManualEntry]
    );

    const handleRefill = useCallback(() => {
      if (isRefilling || !task.canBeRefilled) return;

      // Find the inventory box matching the task
      const validatedType = validateTaskType(task.type);
      const matchedBox = boxes?.find(
        (box) =>
          box.inventory_type?.toLowerCase() === validatedType.toLowerCase() &&
          String(box.box_no) === String(task.position) &&
          box.item_name === task.item_name
      );

      if (!matchedBox) {
        showNotification("error", "No matching inventory box found");
        return;
      }

      // Set the selected box and show the modal
      setSelectedBoxForRefill(matchedBox);
      setShowRefillModal(true);
    }, [task, isRefilling, boxes, showNotification]);

    const handleRefillSuccess = useCallback(
      (updatedBox) => {
        setIsRefilling(true);

        // First update the task directly for immediate UI feedback
        const updatedTask = {
          ...task,
          isOk: true,
          errorMessage: null,
          canBeRefilled: false,
        };

        // Update tasks array to trigger UI refresh
        const updatedTasks = tasks.map((t) =>
          t.id === task.id ? updatedTask : t
        );

        // Then do the proper backend update
        updateTaskStatusAfterRefill(
          updatedBox,
          matchInventoryWithTasks,
          updatedTasks,
          showNotification,
          refreshInventory
        ).finally(() => {
          setIsRefilling(false);
          setExpanded(false); // Close the accordion after successful refill
          setShowRefillModal(false); // Close the modal
        });
      },
      [task, tasks, matchInventoryWithTasks, showNotification, refreshInventory]
    );

    // Toggle swap type selection
    const handleSwapTypeSelect = useCallback((type) => {
      setActiveSwapType((prev) => {
        const newType = prev === type ? null : type;
        return newType;
      });
    }, []);

    // Determine if we show error or success styling
    const rowStyle = useMemo(() => {
      // Base styles
      const baseStyle = {
        display: "flex",
        alignItems: "center",
        p: 2,
        mb: 2,
        borderRadius: 2,
        transition: "all 0.3s ease",
        cursor: "pointer", // Always clickable
      };

      // Ready ingredients styling (green theme)
      if (task.isOk) {
        return {
          ...baseStyle,
          border: "2px solid",
          borderColor: "success.main",
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(76, 175, 80, 0.12)"
              : "rgba(76, 175, 80, 0.08)",
          boxShadow: "0 2px 8px rgba(76, 175, 80, 0.2)",
          "&:hover": {
            transform: "translateY(-3px)",
            boxShadow: "0 4px 16px rgba(76, 175, 80, 0.3)",
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(76, 175, 80, 0.18)"
                : "rgba(76, 175, 80, 0.12)",
            borderColor: "success.dark",
          },
        };
      }

      // Not ready ingredients styling (error theme)
      return {
        ...baseStyle,
        border: "2px solid",
        borderColor: isUnavailable ? "error.main" : "warning.main",
        bgcolor:
          theme.palette.mode === "dark"
            ? isUnavailable
              ? "rgba(244, 67, 54, 0.12)"
              : "rgba(255, 152, 0, 0.12)"
            : isUnavailable
            ? "rgba(244, 67, 54, 0.08)"
            : "rgba(255, 152, 0, 0.08)",
        boxShadow: isUnavailable
          ? "0 2px 8px rgba(244, 67, 54, 0.2)"
          : "0 2px 8px rgba(255, 152, 0, 0.2)",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: isUnavailable
            ? "0 4px 16px rgba(244, 67, 54, 0.3)"
            : "0 4px 16px rgba(255, 152, 0, 0.3)",
          bgcolor:
            theme.palette.mode === "dark"
              ? isUnavailable
                ? "rgba(244, 67, 54, 0.18)"
                : "rgba(255, 152, 0, 0.18)"
              : isUnavailable
              ? "rgba(244, 67, 54, 0.12)"
              : "rgba(255, 152, 0, 0.12)",
          borderColor: isUnavailable ? "error.dark" : "warning.dark",
        },
      };
    }, [task.isOk, theme.palette.mode, isUnavailable]);

    // Handle row click for unassigned tasks
    const handleRowClick = useCallback(() => {
      // Track if task was ready when user opened it for editing
      setWasReadyWhenOpened(task.isOk);
      setIsUserEditing(true);
      setExpanded(true);
    }, [task.isOk]);

    // Get container type info for current task
    const taskTypeKey = validateTaskType(task.type)?.toLowerCase();
    const taskTypeInfo = taskTypeKey
      ? getContainerTypeInfo(theme)[taskTypeKey]
      : null;

    return (
      <>
        <Paper
          elevation={0}
          sx={{
            ...rowStyle,
            cursor: "pointer", // Always show pointer cursor to indicate clickability
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            flexWrap: "wrap",
            gap: 1,
            p: { xs: 2, sm: 2 },
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: 3,
            },
          }}
          onClick={handleRowClick}
        >
          <Box
            sx={{
              display: "flex",
              width: "100%",
              alignItems: "center",
              mb: { xs: 1, sm: 0 },
            }}
          >
            <TaskStatusIcon isOk={task.isOk} theme={theme} />
            <TaskInfo task={task} isReady={task.isOk} />
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1,
              width: { xs: "100%", sm: "auto" },
              justifyContent: { xs: "flex-start", sm: "flex-end" },
              mt: { xs: 1, sm: 0 },
            }}
          >
            {task.errorMessage && !expanded && (
              <ErrorMessageChip
                errorMessage={task.errorMessage}
                onClick={handleRefill}
                showRefill={task.canBeRefilled}
              />
            )}

            {task.position && (
              <Chip
                icon={taskTypeInfo?.icon}
                label={`${taskCategory}: ${task.position}`}
                color={task.isOk ? "success" : "primary"}
                variant={task.isOk ? "filled" : "outlined"}
                size="medium"
                sx={{ fontWeight: "medium" }}
              />
            )}
          </Box>
        </Paper>

        {/* Accordion expansion for swap options */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Paper
            elevation={3}
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: "0 0 16px 16px",
              mt: -2,
              borderTop: "none",
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(66, 66, 66, 0.7)"
                  : "rgba(250, 250, 250, 0.9)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  fontSize: "1.1rem",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <FoodBankIcon sx={{ mr: 1, color: "primary.main" }} />
                Where to place: {task.item_name}
              </Typography>
              <IconButton size="small" onClick={handleClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Improved Container Type Selection */}
            <ContainerTypeSelection
              swapTypes={swapTypes}
              activeType={activeSwapType}
              onSelect={handleSwapTypeSelect}
              theme={theme}
            />

            {/* Improved Box Selection Grid */}
            {activeSwapType && (
              <BoxSelection
                boxes={boxes}
                taskType={activeSwapType}
                onSelect={handleBoxSelection}
                stationSelected={stationSelected}
                showNotification={showNotification}
                currentTasks={tasks}
              />
            )}

            {!activeSwapType && (
              <Box
                sx={{
                  textAlign: "center",
                  py: 3,
                  borderRadius: 2,
                  bgcolor: "rgba(0,0,0,0.03)",
                }}
              >
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Choose a container type from above
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  Different containers work best for different ingredients
                </Typography>
              </Box>
            )}
          </Paper>
        </Collapse>

        {/* Inventory Refill Modal */}
        <CustomModal
          open={showRefillModal}
          onClose={() => setShowRefillModal(false)}
          title="Refill Inventory"
        >
          {selectedBoxForRefill && (
            <InventoryEditBox
              selectedBox={selectedBoxForRefill}
              setShowModal={() => setShowRefillModal(false)}
              kitchenId={kitchenDetail?.kitchenId}
              userId={user?.me?.id}
              refetch={refreshInventory}
              initialItemName={task.item_name}
              onSuccess={handleRefillSuccess}
            />
          )}
        </CustomModal>
      </>
    );
  }
);

TaskRow.displayName = "TaskRow";

TaskRow.propTypes = {
  task: PropTypes.object.isRequired,
  tasks: PropTypes.array.isRequired,
  onManualEntry: PropTypes.func.isRequired,
  onTaskSwap: PropTypes.func.isRequired,
  inventoryData: PropTypes.array,
  totaltasks: PropTypes.array,
  matchInventoryWithTasks: PropTypes.func.isRequired,
  boxes: PropTypes.array,
  stationSelected: PropTypes.string,
  showNotification: PropTypes.func,
  refreshInventory: PropTypes.func,
  isUnavailable: PropTypes.bool,
};

export default TaskRow;
